import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { UserPlus, Users, Heart, HandHelping, ArrowUpRight, PartyPopper } from "lucide-react";

const GOOGLE_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSeA0Z9S8s-q3Lw-S_cBjfPtGsADbNQbE3KTG9ndPqObKX5__w/viewform?embedded=true";
const GOOGLE_FORM_LINK =
  "https://docs.google.com/forms/d/e/1FAIpQLSeA0Z9S8s-q3Lw-S_cBjfPtGsADbNQbE3KTG9ndPqObKX5__w/viewform?usp=sharing";

const ways = [
  {
    icon: PartyPopper,
    n: "01",
    title: "Come to a gathering",
    text: "The easiest way to plug in. Just show up — no pressure, no expectations.",
  },
  {
    icon: UserPlus,
    n: "02",
    title: "Invite your friends",
    text: "This whole thing grows one invite at a time. Bring people with you.",
  },
  {
    icon: HandHelping,
    n: "03",
    title: "Volunteer to serve",
    text: "Setup, welcome, food, prayer — every gathering runs on students who serve.",
  },
  {
    icon: Users,
    n: "04",
    title: "Help lead",
    text: "Want to help shape what this becomes? We're looking for student leaders.",
  },
  {
    icon: Heart,
    n: "05",
    title: "Pray for RVA Students",
    text: "Nothing happens without prayer. Pray for the students, the city, the mission.",
  },
];

const GetInvolvedPage = () => {
  return (
    <>
      <PageHeader
        overline="Jump In"
        title="Get"
        accent="Involved."
        subtitle="You do not have to have everything figured out to get involved. Just show up, bring people with you, and be part of what God is doing."
        testId="involved-header"
        bgImage="https://images.unsplash.com/photo-1584535250253-082add1cd727?fm=jpg&q=85&w=2400&auto=format&fit=crop&ixlib=rb-4.1.0"
      />

      <section className="relative pb-24 md:pb-32" data-testid="involved-list">
        <div className="glow-green top-40 -right-40 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="border-t border-white/10">
            {ways.map((w, i) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.title}
                  className="group grid grid-cols-12 gap-6 items-center py-8 md:py-12 border-b border-white/10 hover:bg-white/[0.04] transition-colors px-4 -mx-4 rounded-2xl"
                  data-testid={`involved-item-${i}`}
                >
                  <div className="col-span-2 md:col-span-1">
                    <p className="font-heading font-black text-2xl md:text-3xl text-white/30 group-hover:text-forest-glow transition-colors">
                      {w.n}
                    </p>
                  </div>
                  <div className="col-span-10 md:col-span-1 flex items-center">
                    <div className={`w-12 h-12 rounded-2xl border border-white/10 flex items-center justify-center ${
                      i % 2 === 0 ? "text-forest-glow" : "text-deepblue-glow"
                    }`}>
                      <Icon size={20} />
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <h3 className="font-heading font-black text-3xl md:text-5xl uppercase tracking-tight leading-none group-hover:translate-x-2 transition-transform">
                      {w.title}
                    </h3>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <p className="text-white/60 text-base md:text-lg leading-relaxed">{w.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sign up form */}
      <section className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden" data-testid="involved-form-section">
        <div className="glow-green top-20 -left-40 opacity-40" />
        <div className="glow-blue bottom-20 -right-40 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 lg:pt-4">
            <p className="overline mb-4">Ready To Plug In?</p>
            <h2 className="font-heading font-black text-4xl md:text-6xl uppercase leading-[0.95] tracking-tight">
              Fill out the<br />
              <span className="text-forest-glow">form</span>.
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
              Whether you want to serve, help lead, host a gathering, or just stay in the loop —
              drop your info and we'll get in touch.
            </p>
            <a
              href={GOOGLE_FORM_LINK}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost mt-8"
              data-testid="involved-form-open-link"
            >
              Open Form In New Tab <ArrowUpRight size={16} />
            </a>
          </div>

          <div className="lg:col-span-7" data-testid="involved-form-wrapper">
            <div className="card-glass p-2 md:p-3 relative overflow-hidden">
              <div className="rounded-2xl overflow-hidden bg-white/[0.04]">
                <iframe
                  src={GOOGLE_FORM_URL}
                  width="100%"
                  height="900"
                  frameBorder="0"
                  marginHeight="0"
                  marginWidth="0"
                  title="Get Involved with RVA Students"
                  data-testid="involved-google-form-iframe"
                  className="w-full block"
                >
                  Loading…
                </iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-16 md:py-24 text-center" data-testid="involved-cta-strip">
        <div className="relative max-w-3xl mx-auto px-6">
          <p className="overline mb-4">Have A Question First?</p>
          <h3 className="font-heading font-black text-3xl md:text-4xl uppercase leading-tight tracking-tight mb-8">
            Reach out anytime.
          </h3>
          <Link to="/contact" className="btn-primary" data-testid="involved-cta-contact">
            Contact Us <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
};

export default GetInvolvedPage;
